import { Button, Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { Iconify, LoadingText } from '@sdf/base';
import CustomModal from '../../Common/CustomModal';
import { useHandleApiCall } from '../../hooks';
import { API_CONSTANTS, CardTypeEnum } from '../../utils';
import { useDeleteBrollVideo } from 'apps/t2c-fe/services/broll-videos/deleteVideo';
import { LightTooltip } from '../avatar-library/PrivateAvatarCard';
import { Styles } from '@t2c/shared-fe';
import { useDeleteAvatar } from 'apps/t2c-fe/services/avatar/deleteAvatar';
import { CustomDialogTitle } from 'apps/t2c-fe/Common/dialog';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import { useDeleteAiMedia } from '../../services/ai-generate/deleteAiMedia';
import { useDeleteCreatedAudio } from '../../hooks/audios';
import { deleteCustomBrolls } from '../../services/custom-broll/deleteCustomBroll';
import { useDeletePaymentMethod } from '../../services/payment/deletePaymentMethod';
import { useDeleteUpscaleMedia } from '../../services/upscale/deleteVideo';
import { useDeleteAiClipperMedia } from '../../services/ai-clipper/deleteVideo';

interface DeleteConfirmModalProps {
  open: boolean;
  id: string;
  name: string;
  handleCancel: () => void;
  refetchData?: Function;
  type?: CardTypeEnum;
}

export const DeleteConfirmModal = ({
  open,
  id,
  name,
  handleCancel,
  refetchData,
  type,
}: DeleteConfirmModalProps) => {
  const { deleteReq } = useHandleApiCall();
  const { mutateAsync, isLoading } = deleteReq(
    API_CONSTANTS.DELETE_VIDEO.key,
    API_CONSTANTS.DELETE_VIDEO.path
  );

  const { mutateAsync: deleteBrollVideo } = useDeleteBrollVideo();
  const { mutateAsync: deleteAvatar } = useDeleteAvatar();
  const { mutateAsync: deleteMedia} = useDeleteAiMedia();
  const { mutateAsync: deleteAudio} = useDeleteCreatedAudio();
  const { mutateAsync: deletePaymentMethod } = useDeletePaymentMethod();
  const { mutateAsync: deleteUpscaleMedia } = useDeleteUpscaleMedia();
  const { mutateAsync: deleteAiClipperMedia } = useDeleteAiClipperMedia();

  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const handleDelete = async () => {
    setLoading(true);
    try {
      if (type === CardTypeEnum.Broll) {
        await deleteBrollVideo(id);
      }
      if (type === CardTypeEnum.LipsyncVideo) {
        await mutateAsync(id);
      }
      if (type === CardTypeEnum.Avatar) {
        await deleteAvatar(id);
      }
      if (type === CardTypeEnum.AIMedia) {
        await deleteMedia({aiMediaId: id});
      }
      if (type === CardTypeEnum.Voiceover) {
        await deleteAudio({id: id});
      }
      if (type === CardTypeEnum.MyBroll) {
        await deleteCustomBrolls({id: id});
      }
      if (type === CardTypeEnum.PaymentMethod) {
        await deletePaymentMethod({id: id});
      }
      if (type === CardTypeEnum.Upscale) {
        await deleteUpscaleMedia(id);
      }
      if (type === CardTypeEnum.AiClipper) {
       await deleteAiClipperMedia(id);
      }
      await refetchData();
      enqueueSnackbar('Deleted successfully!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to delete!', { variant: 'error' });
      console.error(error);
    }
    setLoading(false);
    handleCancel();
  };

  return (
    <CustomModal open={open}>
      <CustomDialogTitle title="Delete" onClose={handleCancel} />
      <Box pt={2} sx={styles.container}>
        <Box
          sx={{
            width: '3rem',
            height: '3rem',
            borderRadius: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Iconify
            icon={'akar-icons:circle-alert'}
            width={'2rem'}
            height={'2rem'}
            color={(theme)=> theme.palette.error.main}
          />
        </Box>
        <Typography mt={2} px={2}>
          Are you sure you want to delete the
        </Typography>

        <Tooltip title={name}>
          <Typography sx={styles.videoName}>
            <strong>{name}</strong>
          </Typography>
        </Tooltip>
        <Box mt={4}>
          {isLoading ? (
            <LoadingText text="Deleting item, please wait..." />
          ) : (
            <>
              <LoadingButton
                onClick={handleDelete}
                variant="outlined"
                sx={{ width: '88px', height: '26px' }}
                loading={loading}
              >
                Yes
              </LoadingButton>
              <Button
                variant="contained"
                onClick={handleCancel}
                sx={{
                  width: '88px',
                  marginLeft: '24px',
                  height: '26px',
                  color: (theme) => theme.palette.text.btnText,
                }}
              >
                No
              </Button>
            </>
          )}
        </Box>
      </Box>
    </CustomModal>
  );
};

const styles: Styles = {
  container: {
    paddingX: 3,
    textAlign: 'center',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  videoName: {
    textOverflow: 'ellipsis',
    maxWidth: '300px',
    overflow: 'hidden',
    cursor: 'pointer',
  },
};
